import React from 'react';
import PropTypes from 'prop-types';
import { Icon, useSnackBar } from '@usealloy/component-library';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import checkPermissions from '~/evaluations/helpers/permissions';

import { formatSelectorSlug, snakeToCaps } from '~/helpers/formatting';
import { tooltipZIndexModifiers } from '~/constants/reactstrap';
import { UserContext } from '~/dashboard/context';

/* ServicesList
 * Displays a list of all services run for specific evaluation.
 * Each service is clickable to display raw response for that service.
 */
const ServicesListPropTypes = {
  evaluation: PropTypes.object.isRequired,
  updateSelectedServiceResponse: PropTypes.func.isRequired,
  addSnackBar: PropTypes.func,
};
class ServicesList extends React.Component {
  constructor(props) {
    super(props);

    this.handleOpenServiceResponseModal = this.handleOpenServiceResponseModal.bind(this);
    this.renderService = this.renderService.bind(this);
  }

  hasPiiPermissions() {
    const { permissions = {} } = this.context || {};
    return checkPermissions(permissions, ['dob', 'ssn']);
  }

  isPiiPermissionsFeatureEnabled() {
    const { featureFlags = {} } = this.context || {};
    return Boolean(featureFlags.forbidUnpermittedAgentsSeeingPiiResponses);
  }

  static isServiceClickable(evaluation, service) {
    const { service_responses: serviceResponses, final_response: response } = evaluation;
    const hasRawResponse = Boolean(
      response.raw_responses[service] && response.raw_responses[service].length,
    );
    const hasServiceResponse = Boolean(serviceResponses[service]);
    return hasServiceResponse || hasRawResponse;
  }

  static getServiceStatus(evaluation, service) {
    const { final_response: response } = evaluation;
    return response.summary.services[service];
  }

  static getStatusIcon(status) {
    if (['executed', 'cached'].indexOf(status) > -1) return 'CircleCheckFilled';
    if (status === 'failed') return 'CircleCloseFilled';
    return 'CircleMinusFilled';
  }

  static getStatusColor(status) {
    if (['executed', 'cached'].indexOf(status) > -1) return 'var(--cl-green-300)';
    if (status === 'failed') return 'var(--cl-red-300)';
    return 'black';
  }

  static getServiceResponseId(evaluation, service) {
    const { service_responses: serviceResponses, final_response: response } = evaluation;
    const hasRawResponse = response.raw_responses[service]
    && response.raw_responses[service].length;
    if (serviceResponses[service]) return serviceResponses[service].service_response_id;
    if (hasRawResponse) return service;
    return 0;
  }

  handleOpenServiceResponseModal(event) {
    const { updateSelectedServiceResponse, evaluation, addSnackBar } = this.props;
    event.preventDefault();
    const target = event.currentTarget;

    const hasFeatureFlag = this.isPiiPermissionsFeatureEnabled();
    const hasPermissions = this.hasPiiPermissions();
    if (hasFeatureFlag && !hasPermissions) {
      addSnackBar({
        id: 'sb-view-service-metadata-permissions',
        title: 'You do not have permission to see this service metadata.',
        type: 'danger',
        duration: 2000,
      });
      return;
    }

    const serviceName = target?.dataset?.serviceName;
    if (!ServicesList.isServiceClickable(evaluation, serviceName)) {
      return;
    }
    let serviceResponse = target?.dataset?.serviceResponse;
    serviceResponse = isNaN(+serviceResponse) ? serviceResponse : +serviceResponse;
    if (serviceResponse) updateSelectedServiceResponse(serviceResponse);
  }


  renderService(service) {
    const { evaluation } = this.props;
    const clickable = ServicesList.isServiceClickable(evaluation, service);

    const status = ServicesList.getServiceStatus(evaluation, service);
    const statusIcon = ServicesList.getStatusIcon(status);
    const statusColor = ServicesList.getStatusColor(status);

    const serviceResponseId = ServicesList.getServiceResponseId(evaluation, service);
    const serviceName = formatSelectorSlug(service);
    const serviceId = `eval-${evaluation.evaluation_id}-service-${serviceName}`;
    const serviceClass = statusIcon === 'CircleCheckFilled' ? 'bold underline' : '';

    return (
      <span key={serviceId}>
        <span
          id={serviceId}
          className={
            `service small-text ${status} ${clickable ? 'clickable' : ''}`
          }
          data-service-response={serviceResponseId}
          data-service-name={service}
          onClick={this.handleOpenServiceResponseModal}
          data-dd-action-name="alloydd-legacy-data-sources"
        >
          <span className="icon">
            {statusIcon ? <Icon icon={statusIcon} size="large" color={statusColor} /> : false}
          </span>

          <span className={serviceClass}>{service}</span>
        </span>

        <Tooltip
          target={serviceId}
          placement="left"
          delay={0}
          modifiers={tooltipZIndexModifiers}
        >
          {snakeToCaps(status)}
        </Tooltip>
      </span>
    );
  }


  render() {
    const { evaluation: { final_response: { summary } } } = this.props;
    const services = Object.keys(summary?.services || {});

    return services.length ? (
      <div className="services-list flex align-start">
        {services.map(this.renderService)}
      </div>
    ) : (
      <div className="services-list">
        <p className="light-grey"><i>No data sources run for this evaluation.</i></p>
      </div>
    );
  }
}
ServicesList.contextType = UserContext;
ServicesList.propTypes = ServicesListPropTypes;

// Functional wrapper to provide snack bar to class component
const ServicesListWithSnackBar = props => {
  const addSnackBar = useSnackBar();
  return <ServicesList {...props} addSnackBar={addSnackBar} />;
};

export { ServicesList as ServicesListBase };
export default ServicesListWithSnackBar;
